// Exhaustion (2024 SRD 5.2): levels 1-6, cumulative. Each level gives -2 to D20 Tests (attack rolls, ability checks and
// saving throws, death saves included) and -5 feet of Speed (movementCore reads the level). A creature dies at level 6.
// A Long Rest removes one level. Stored as one `condition:exhaustion` effect per creature carrying its `level`; it lasts
// until removed, in combat or not, like the other DM conditions.
export const EXHAUSTION = 'condition:exhaustion';
export const MAX_EXHAUSTION_LEVEL = 6;

const onCreature = (effect, id) => effect?.definitionId === EXHAUSTION && String(effect.targetCombatantId) === String(id);

export const exhaustionLevel = (combat, combatantId) => Math.min(MAX_EXHAUSTION_LEVEL, Math.max(0,
  ...(combat?.activeEffects || []).filter(effect => onCreature(effect, combatantId)).map(effect => Number(effect.level ?? effect.exhaustionLevel) || 0)));

/** The D20 Test penalty as a roll modifier with no die: `{ flat }` instead of `{ sides, sign }`. */
export const exhaustionRollModifiers = (combat, combatantId) => {
  const level = exhaustionLevel(combat, combatantId);
  return level ? [{ id: 'exhaustion', name: `Exhaustion ${level}`, flat: -2 * level }] : [];
};

export const createExhaustionEffect = ({ targetCombatantId, level }) => ({
  id: `dm:exhaustion:${targetCombatantId}`, definitionId: EXHAUSTION, name: `Exhaustion ${level}`, icon: '😩', userVisible: true,
  targetCombatantId, sourceCombatantId: targetCombatantId, sourceFeature: 'dm:exhaustion', level,
  stackKey: `${EXHAUSTION}:${targetCombatantId}`, stackPolicy: 'replace', expiration: { type: 'manual' }, removeOnSourceRemoval: false,
  description: `Exhaustion level ${level}: -${2 * level} to D20 Tests and -${5 * level} feet of Speed.${level >= MAX_EXHAUSTION_LEVEL ? ' Level 6: the creature dies.' : ''} A Long Rest removes one level.`,
});

/** Sets the creature's level (0 removes it), replacing every Exhaustion effect it has. */
export const withExhaustionLevel = (activeEffects = [], combatantId, level) => [
  ...activeEffects.filter(effect => !onCreature(effect, combatantId)),
  ...(level > 0 ? [createExhaustionEffect({ targetCombatantId: String(combatantId), level: Math.min(MAX_EXHAUSTION_LEVEL, level) })] : []),
];

/** A rest removes one level from each listed creature (any Long Rest; a Short Rest for Tireless). Returns null when nobody listed is Exhausted. */
export const afterLongRestExhaustion = (activeEffects = [], combatantIds = []) => {
  const exhausted = combatantIds.filter(id => exhaustionLevel({ activeEffects }, id) > 0);
  return exhausted.length ? exhausted.reduce((effects, id) => withExhaustionLevel(effects, id, exhaustionLevel({ activeEffects }, id) - 1), activeEffects) : null;
};
